import { useState, useCallback, useMemo, useEffect } from "react";
import { Box, useTheme, useMediaQuery } from "@mui/material";

import AiSidebar from "@/ai/AiSidebar";
import AiChatSingleThreaded from "@/ai/AiChatSingleThreaded";

import WidgetZone from "@/ai/WidgetZone";
import ResizableSplitPane from "@/ai/ResizableSplitPane";
import { getWorkflowConfig } from "@/ai/workflowRegistry";
import { getToolCallResult, getToolCallResultType } from "@/ai/utils";
import { capitalizeFirstLetter, downloadFile } from "@/utils";
import Analytics from "@/Analytics";
import { request } from "@/Api";
import ActionsPanel from "@/ai/panels/ActionsPanel";
import BidPreviewPanel from "@/ai/panels/BidPreviewPanel";
import WizardPanel from "@/ai/panels/WizardPanel";

import useThreadManager from "@/ai/hooks/useThreadManager";
import useCanvasWorkflow from "@/ai/hooks/useCanvasWorkflow";
import useBidWorkflow from "@/ai/hooks/useBidWorkflow";

/**
 * AiAssistant — the unified, composable AI container.
 *
 * Thin orchestrator that composes:
 *  - useThreadManager: thread list, selection, workflow switching
 *  - useCanvasWorkflow: canvas state for brief/wrap_report
 *  - useBidWorkflow: bid state for bid import
 *  - AiSidebar, AiChatSingleThreaded, WidgetZone
 *
 * Props:
 *  - modelType: "project" | null (global)
 *  - modelInstanceId: e.g. projectId
 *  - availableWorkflows: string[] — which workflow types to show in sidebar
 *  - initialAgent: string | null — auto-select this agent type on open
 *  - initialPrompt: string — pre-fill the first message
 *  - onClose: () => void
 */
export default function AiAssistant({
    modelType,
    modelInstanceId,
    modelLabel,
    availableWorkflows = [
        "brief",
        // "bid_create", // hidden for this branch
        // "bid",        // hidden for this branch
        "timeline",
        "wrap_report",
    ],
    initialAgent = null,
    initialPrompt = "",
    hideSidebar = false,
    onClose,
}) {
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down("md"));

    // ─── Workflow hooks ─────────────────────────────────────────────────
    const canvas = useCanvasWorkflow();
    const bid = useBidWorkflow({ modelInstanceId });

    const resetAllWidgets = useCallback(() => {
        canvas.reset();
        bid.reset();
        setMessages([]);
        setNewThreadData(null);
        setWaiting(false);
    }, [canvas, bid]);

    const threadManager = useThreadManager({
        modelType,
        modelInstanceId,
        initialAgent,
        onResetWidgets: resetAllWidgets,
    });

    // ─── Shared chat state ──────────────────────────────────────────────
    const [messages, setMessages] = useState([]);
    const [newThreadData, setNewThreadData] = useState(null);
    const [waiting, setWaiting] = useState(false);

    // Clear newThreadData after child has consumed it (one render cycle).
    // Prevents stale re-sends on chat pane remount (collapse/expand).
    useEffect(() => {
        if (newThreadData) {
            setNewThreadData(null);
        }
    }, [newThreadData]);

    // ─── Derived state ──────────────────────────────────────────────────
    const workflowConfig = useMemo(
        () => getWorkflowConfig(threadManager.activeAgentType),
        [threadManager.activeAgentType],
    );

    // Auto-send initialPrompt for router agent (e.g., prompt bar text)
    const [initialPromptSent, setInitialPromptSent] = useState(false);
    useEffect(() => {
        if (initialPromptSent) return;
        if (!initialPrompt) return;
        if (threadManager.activeAgentType !== "router") return;
        if (threadManager.threadsLoading) return;

        setInitialPromptSent(true);
        setNewThreadData({ type: "text", input: initialPrompt, files: [] });
    }, [
        initialPrompt,
        threadManager.activeAgentType,
        threadManager.threadsLoading,
        initialPromptSent,
    ]);

    // ─── Project context (for welcome message) ──────────────────────────
    const [projectData, setProjectData] = useState(null);
    useEffect(() => {
        if (modelType !== "project" || !modelInstanceId) return;
        request
            .get(`/projects/${modelInstanceId}`)
            .then((res) => setProjectData(res.data))
            .catch(() => {});
    }, [modelType, modelInstanceId]);

    const welcomeContent = useMemo(() => {
        if (threadManager.activeAgentType === "router") return null;
        if (!projectData) {
            // Global mode — use workflow-level welcome content if available
            return workflowConfig.welcomeContent || null;
        }
        const roleName = workflowConfig.label || "Assistant";
        const { name, docket, description, start_date, end_date, status } =
            projectData;
        const statusName = status?.name || projectData.status_name;
        const details = [
            docket && `**Docket:** ${docket}`,
            statusName && `**Status:** ${statusName}`,
            start_date && `**Start:** ${start_date}`,
            end_date && `**End:** ${end_date}`,
        ].filter(Boolean);

        return {
            role: roleName,
            projectName: name || modelLabel,
            description: description || null,
            details,
        };
    }, [
        projectData,
        workflowConfig.label,
        modelLabel,
        threadManager.activeAgentType,
    ]);

    // ─── More derived state ─────────────────────────────────────────────
    const layoutDefaults = workflowConfig.layoutDefaults || {};
    const hasWidgets = workflowConfig.panels.length > 0;
    const isBidWorkflow = threadManager.activeAgentType === "bid";
    const threadStarted = messages.length > 0;

    const projectCreated =
        messages.filter(getToolCallResult("project_id")).length > 0;
    const bidCreated = messages.filter(getToolCallResult("bid_id")).length > 0;
    const briefCreated = canvas.canvasUpdates.length > 0;
    const [shortcutAdded, setShortcutAdded] = useState(false);

    // Track project ID dynamically — starts as modelInstanceId (null for intake),
    // gets set when add_project creates a project in the intake flow.
    const [dynamicProjectId, setDynamicProjectId] = useState(modelInstanceId);

    const sources = useMemo(() => {
        return messages.filter((m) => m.type === "file").map((m) => m.file);
    }, [messages]);

    // ─── Message router ─────────────────────────────────────────────────
    const handleNewMessage = useCallback(
        (message) => {
            setMessages((prev) => [...prev, message]);

            // Router agent: handle workflow routing
            const routingResult = getToolCallResult("agent_type")(message);
            if (routingResult) {
                // TODO: Re-enable taskSummary auto-thread-start when file uploader flow is decoupled
                // const taskSummary = getToolCallResult("task_summary")(message)
                threadManager.startNewWorkflow(routingResult);
                return;
            }

            // Canvas workflow: handle canvas creation/updates
            const canvasId = getToolCallResult("canvas_id")(message);
            if (canvasId) {
                const canvasName = getToolCallResult("name")(message);
                canvas.handleCanvasToolResult(canvasId, canvasName);
            }

            // Intake workflow: bind thread to project after add_project
            const projectId = getToolCallResult("project_id")(message);
            if (projectId && threadManager.activeAgentType === "intake") {
                setDynamicProjectId(parseInt(projectId));
                const threadId = message.thread_id;
                if (threadId) {
                    request
                        .patch(
                            `/threads/${threadId}/bind-model?model_type=project&model_instance_id=${projectId}`,
                        )
                        .catch((err) =>
                            console.error(
                                "Failed to bind thread to project:",
                                err,
                            ),
                        );
                }
            }

            // Bid workflow: handle bid preview and wizard config
            const bidPreview = getToolCallResult("bid_preview")(message);
            if (bidPreview) {
                bid.handleBidPreviewResult(bidPreview);
            }

            const wizardConfigResult =
                getToolCallResult("wizard_config")(message);
            if (wizardConfigResult) {
                bid.handleWizardConfigResult(wizardConfigResult);
            }
        },
        [canvas, bid, threadManager],
    );

    const handleNewMessageSnapshot = useCallback(
        (data) => {
            if (data.type !== "input_json") return;
            const snapshot = data.snapshot;

            if (snapshot.canvas_content) {
                canvas.handleCanvasSnapshot(snapshot.canvas_content);
            }
            if (snapshot.bid_preview) {
                bid.handleBidSnapshot(snapshot.bid_preview);
            }
            if (snapshot.wizard_config) {
                bid.handleWizardConfigSnapshot(snapshot.wizard_config);
            }
        },
        [canvas, bid],
    );

    const handleLoadThread = useCallback(
        (thread) => {
            canvas.reset();
            bid.reset();
            setMessages(thread.messages);
            canvas.loadCanvasesFromThread(thread.canvases);

            // Restore bid preview/wizard from the last tool call results in the thread
            restoreBidStateFromMessages(thread.messages);
        },
        [canvas, bid],
    );

    /**
     * Scans messages in reverse to find and restore the most recent
     * bid_preview and wizard_config tool call results.
     */
    const restoreBidStateFromMessages = useCallback(
        (messages) => {
            let foundPreview = false;
            let foundConfig = false;

            for (let i = messages.length - 1; i >= 0; i--) {
                if (foundPreview && foundConfig) break;

                const message = messages[i];

                if (!foundPreview) {
                    const bidPreview =
                        getToolCallResult("bid_preview")(message);
                    if (bidPreview) {
                        bid.handleBidPreviewResult(bidPreview);
                        foundPreview = true;
                    }
                }

                if (!foundConfig) {
                    const wizardConfigResult =
                        getToolCallResult("wizard_config")(message);
                    if (wizardConfigResult) {
                        bid.handleWizardConfigResult(wizardConfigResult);
                        foundConfig = true;
                    }
                }
            }
        },
        [bid],
    );

    const handleError = useCallback(() => {
        // no-op: legacy file uploader removed
    }, []);

    // ─── File attachment callback ────────────────────────────────────────
    const handleFilesAttached = useCallback(
        (files) => {
            canvas.setCanvasLoading(true);

            if (threadManager.activeAgentType === "bid" && files?.length > 0) {
                setWaiting(true);
                bid.fetchPreviewData(files[0]).finally(() => setWaiting(false));
            }

            // Notify Project page to refresh AI files list
            if (files?.length > 0) {
                setTimeout(() => {
                    window.dispatchEvent(
                        new CustomEvent("threadFiles:updated"),
                    );
                }, 2000);
            }
        },
        [threadManager, canvas, bid],
    );

    // ─── Actions (brief/wrap_report) ────────────────────────────────────
    const handleDownloadBrief = useCallback(() => {
        if (!canvas.selectedCanvas) return;
        downloadFile(`/brief-export/${canvas.selectedCanvas.id}`);
        Analytics.trackEngagement(Analytics.AI_INTAKE_DOWNLOAD_BRIEF);
    }, [canvas.selectedCanvas]);

    const handleDownloadSheet = useCallback(() => {
        if (!canvas.selectedCanvas) return;
        downloadFile(`/brief-export-sheet/${canvas.selectedCanvas.id}`);
    }, [canvas.selectedCanvas]);

    const handleCreateProject = useCallback(() => {
        setNewThreadData({
            type: "text",
            files: [],
            input: "Create a project based on the provided brief",
        });
        Analytics.trackEngagement(Analytics.AI_INTAKE_CREATE_PROJECT);
    }, []);

    const handleCreateBid = useCallback(() => {
        setNewThreadData({
            type: "text",
            files: [],
            input: "Create a bid from template",
        });
        Analytics.trackEngagement(Analytics.AI_INTAKE_CREATE_BID);
    }, []);

    const handleAddShortcut = useCallback((createdShortcut) => {
        setShortcutAdded(true);
        window.dispatchEvent(
            new CustomEvent("shortcut:created", {
                detail: createdShortcut,
            }),
        );
    }, []);

    const shortcutDefaults = useMemo(() => {
        console.log(canvas.selectedCanvas);
        if (!canvas.selectedCanvas) return null;
        return {
            name: canvas.selectedCanvas.name || "AI Canvas",
            url: `${window.location.origin}/canvas/${canvas.selectedCanvas.id}`,
            shortcut_type: "canvas",
        };
    }, [canvas.selectedCanvas]);

    // ─── Wizard callbacks (bid) ─────────────────────────────────────────
    const handleWizardSubmit = useCallback((config) => {
        setNewThreadData({
            type: "text",
            input: `I have updated the wizard configuration: ${JSON.stringify(config)}`,
            files: [],
        });
    }, []);

    const handleRunPreview = useCallback(
        (config) => {
            setWaiting(true);
            bid.runWizardPreview(config).finally(() => setWaiting(false));
        },
        [bid],
    );

    // ─── Build panel props ──────────────────────────────────────────────
    const panelProps = useMemo(
        () => ({
            canvas: {
                canvasUpdates: canvas.canvasUpdates,
                newCanvasData: canvas.newCanvasData,
                canvasLoading: canvas.canvasLoading,
                onChange: () => {},
                onLoad: () => {},
                onCanvasSelected: canvas.setSelectedCanvas,
            },
            actions: {
                waiting,
                briefCreated,
                projectCreated,
                bidCreated,
                shortcutAdded,
                onDownloadBrief: handleDownloadBrief,
                onDownloadSheet: handleDownloadSheet,
                onCreateProject: handleCreateProject,
                onCreateBid: handleCreateBid,
                onAddShortcut: handleAddShortcut,
                onClose,
                agentType: threadManager.activeAgentType,
                shortcutDefaults,
                projectId: dynamicProjectId,
            },
            links: {
                links: getLinks(messages),
            },
            bidPreview: {
                wizardBidData: bid.wizardBidData,
                llmBidData: bid.llmBidData,
                highlightDepth: bid.highlightDepth,
                onWizardBidChange: bid.setWizardBidData,
                onLlmBidChange: bid.setLlmBidData,
                onSubmitSuccess: onClose,
            },
            wizard: {
                previewData: bid.previewData,
                initialConfig: bid.wizardConfig,
                onPrimarySubmit: handleRunPreview,
                onSecondarySubmit: handleWizardSubmit,
                onCancel: () => {},
                isImporting: waiting,
                onConfigChange: handleRunPreview,
                onHighlightGroupDepth: bid.setHighlightDepth,
                isSheetFile: bid.isSheetFile,
            },
        }),
        [
            canvas.canvasUpdates,
            canvas.newCanvasData,
            canvas.canvasLoading,
            canvas.setSelectedCanvas,
            bid.wizardBidData,
            bid.llmBidData,
            bid.highlightDepth,
            bid.previewData,
            bid.wizardConfig,
            bid.setWizardBidData,
            bid.setLlmBidData,
            bid.setHighlightDepth,
            waiting,
            briefCreated,
            projectCreated,
            bidCreated,
            shortcutAdded,
            shortcutDefaults,
            messages,
            handleDownloadBrief,
            handleDownloadSheet,
            handleCreateProject,
            handleCreateBid,
            handleAddShortcut,
            handleRunPreview,
            handleWizardSubmit,
            onClose,
            threadManager.activeAgentType,
            dynamicProjectId,
        ],
    );

    // ─── Render ─────────────────────────────────────────────────────────
    return (
        <Box
            sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                borderRadius: 3,
                overflow: "hidden",
            }}
        >
            {/* Main Layout — Sidebar is the outermost left pane unless hideSidebar is set */}
            <Box sx={{ flex: 1, overflow: "hidden" }}>
                {hideSidebar ? (
                    /* No sidebar — render right-side content directly */
                    renderMainContent()
                ) : (
                    <ResizableSplitPane
                        orientation="horizontal"
                        initialSplit={layoutDefaults.sidebarSplit ?? 0.2}
                        gap={0}
                        firstTitle="Threads"
                        secondTitle="Chat"
                    >
                        {/* Shared Sidebar — never remounts */}
                        <AiSidebar
                            threads={threadManager.threads}
                            activeThreadId={threadManager.activeThread?.id}
                            availableWorkflows={availableWorkflows}
                            sources={sources}
                            onSelectThread={threadManager.selectThread}
                            onNewChat={threadManager.startNewChat}
                            onNewWorkflow={threadManager.startNewWorkflow}
                            onAddSource={() => {}}
                            defaultTab={
                                !threadManager.threadsLoading &&
                                !threadManager.activeThread
                                    ? 1
                                    : 0
                            }
                        />

                        {/* Right content — renders via renderMainContent() */}
                        {renderMainContent()}
                    </ResizableSplitPane>
                )}
            </Box>
        </Box>
    );

    /**
     * Renders the main content area (right side of sidebar split).
     * Extracted so it can be used both inside ResizableSplitPane and standalone (hideSidebar).
     */
    function renderMainContent() {
        if (isBidWorkflow) {
            return (
                /* ─── Bid: [Chat / Wizard] | [Preview+Actions] ─── */
                <ResizableSplitPane
                    key="bid-layout"
                    orientation="horizontal"
                    initialSplit={layoutDefaults.mainSplit ?? 0.5}
                    firstTitle="Chat"
                    secondTitle="Bid Preview"
                >
                    {/* Column 1: Chat over Wizard */}
                    <ResizableSplitPane
                        orientation="vertical"
                        initialSplit={layoutDefaults.wizardSplit ?? 0.6}
                        firstTitle="Chat"
                        secondTitle="Manual Configuration"
                    >
                        <Box
                            sx={{
                                height: "100%",
                                overflow: "hidden",
                                background: theme.palette.background.default,
                            }}
                        >
                            <AiChatSingleThreaded
                                defaultThreadId={
                                    threadManager.activeThread?.id || null
                                }
                                toolSet={workflowConfig.toolSet}
                                modelType={modelType}
                                modelInstanceId={modelInstanceId}
                                modelLabel={modelLabel}
                                agentType={threadManager.activeAgentType}
                                newThreadData={newThreadData}
                                onNewMessage={handleNewMessage}
                                onNewMessageSnapshot={handleNewMessageSnapshot}
                                onLoadThread={handleLoadThread}
                                onNewThread={
                                    threadManager.handleNewThreadCreated
                                }
                                onError={handleError}
                                onWaiting={() => setWaiting(true)}
                                onStopWaiting={() => setWaiting(false)}
                                suggestions={false}
                                enableMentions={
                                    workflowConfig.enableMentions ?? false
                                }
                                welcomeContent={welcomeContent}
                                onFilesAttached={handleFilesAttached}
                            />
                        </Box>

                        {/* Bottom: Manual Configuration */}
                        <Box sx={{ height: "100%", overflow: "auto" }}>
                            <WizardPanel {...panelProps.wizard} />
                        </Box>
                    </ResizableSplitPane>

                    {/* Column 2: Bid Preview over Actions */}
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            height: "100%",
                            overflow: "hidden",
                            gap: "6px",
                        }}
                    >
                        <Box
                            sx={{
                                flex: 1,
                                minHeight: 0,
                                overflow: "hidden",
                                borderRadius: 2,
                            }}
                        >
                            <BidPreviewPanel {...panelProps.bidPreview} />
                        </Box>
                        <Box
                            sx={{
                                flexShrink: 0,
                                px: 2,
                                py: 1,
                                background: theme.palette.background.default,
                                borderRadius: 2,
                            }}
                        >
                            <ActionsPanel {...panelProps.actions} horizontal />
                        </Box>
                    </Box>
                </ResizableSplitPane>
            );
        }

        // Intake before first message — show chat-only with suggestions
        // Only for new threads; existing threads go straight to canvas layout to avoid remount
        const isIntakePreCanvas =
            threadManager.activeAgentType === "intake" &&
            !threadStarted &&
            !threadManager.activeThread;

        if (hasWidgets && !isIntakePreCanvas) {
            return (
                /* ─── Canvas workflows: Chat | Canvas+Actions ─── */
                <ResizableSplitPane
                    key="canvas-layout"
                    orientation="horizontal"
                    initialSplit={layoutDefaults.mainSplit ?? 0.45}
                    firstTitle="Chat"
                    secondTitle="Canvas"
                    firstPaneRadius="0 8px 8px 0"
                >
                    <Box
                        sx={{
                            height: "100%",
                            overflow: "hidden",
                            background: theme.palette.background.default,
                        }}
                    >
                        <AiChatSingleThreaded
                            key={threadManager.activeAgentType}
                            defaultThreadId={
                                threadManager.activeThread?.id || null
                            }
                            toolSet={workflowConfig.toolSet}
                            modelType={modelType}
                            modelInstanceId={modelInstanceId}
                            modelLabel={modelLabel}
                            agentType={threadManager.activeAgentType}
                            newThreadData={newThreadData}
                            onNewMessage={handleNewMessage}
                            onNewMessageSnapshot={handleNewMessageSnapshot}
                            onLoadThread={handleLoadThread}
                            onNewThread={threadManager.handleNewThreadCreated}
                            onError={handleError}
                            onWaiting={() => setWaiting(true)}
                            onStopWaiting={() => setWaiting(false)}
                            suggestions={false}
                            enableMentions={
                                workflowConfig.enableMentions ?? false
                            }
                            welcomeContent={welcomeContent}
                            onFilesAttached={handleFilesAttached}
                        />
                    </Box>

                    {/* Canvas column: WidgetZone + Actions */}
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            height: "100%",
                            overflow: "hidden",
                            gap: "6px",
                        }}
                    >
                        <Box
                            sx={{
                                flex: 1,
                                minHeight: 0,
                                overflow: "hidden",
                            }}
                        >
                            <WidgetZone
                                workflowConfig={workflowConfig}
                                panelProps={panelProps}
                            />
                        </Box>
                        {panelProps.actions && (
                            <Box
                                sx={{
                                    flexShrink: 0,
                                    px: 2,
                                    py: 1,
                                    background:
                                        theme.palette.background.default,
                                    borderRadius: 2,
                                }}
                            >
                                <ActionsPanel
                                    {...panelProps.actions}
                                    horizontal
                                />
                            </Box>
                        )}
                    </Box>
                </ResizableSplitPane>
            );
        }

        // ─── Router / no-widget: chat only ───
        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    overflow: "hidden",
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        overflow: "hidden",
                        background: theme.palette.background.default,
                        borderRadius: "0 8px 0 0",
                    }}
                >
                    <AiChatSingleThreaded
                        key={threadManager.activeAgentType}
                        defaultThreadId={threadManager.activeThread?.id || null}
                        toolSet={workflowConfig.toolSet}
                        modelType={modelType}
                        modelInstanceId={modelInstanceId}
                        modelLabel={modelLabel}
                        agentType={threadManager.activeAgentType}
                        newThreadData={newThreadData}
                        onNewMessage={handleNewMessage}
                        onNewMessageSnapshot={handleNewMessageSnapshot}
                        onLoadThread={handleLoadThread}
                        onNewThread={threadManager.handleNewThreadCreated}
                        onError={handleError}
                        onWaiting={() => setWaiting(true)}
                        onStopWaiting={() => setWaiting(false)}
                        title={
                            workflowConfig.title ||
                            "How can I help with this project?"
                        }
                        description={workflowConfig.description || null}
                        suggestions={workflowConfig.suggestions || null}
                        enableMentions={workflowConfig.enableMentions ?? false}
                        welcomeContent={welcomeContent}
                        onFilesAttached={handleFilesAttached}
                    />
                </Box>

                {/* Actions — intake pre-canvas shows close-only */}
                <Box
                    sx={{
                        flexShrink: 0,
                        px: 2,
                        py: 1,
                        background: theme.palette.background.default,
                        borderRadius: "0 0 8px 0",
                    }}
                >
                    {isIntakePreCanvas ? (
                        <ActionsPanel onClose={onClose} horizontal />
                    ) : (
                        <ActionsPanel {...panelProps.actions} horizontal />
                    )}
                </Box>
            </Box>
        );
    }
}

/**
 * Extracts unique URLs from tool_result messages.
 */
function getLinks(messages) {
    const urlSet = {};
    messages.filter(getToolCallResult("url")).forEach((message) => {
        const url = getToolCallResult("url")(message);
        const name = getToolCallResult("name")(message);
        const type = capitalizeFirstLetter(getToolCallResultType(message));
        urlSet[url] = { name, type, url };
    });
    return Object.values(urlSet);
}
